import type { RequestError } from '@swf/core/api/request';
import { StatusCode } from '@swf/core/enums/statusCode';
import type { EffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';
import type {
  CasinoGame,
  CasinoGameInstance,
  CasinoInterruptedGame,
  CasinoTable,
  PlayerFSB
} from '@swf/core/types/CasinoGame';
import { JoinReason, PlayType } from '@swf/core/types/CasinoGame';
import { closeWindow, getWindow, isRedirected } from '@swf/core/utils/casino';

const play = async (
  {
    table,
    raceId,
    game,
    joinReason = JoinReason.Play,
    playType = PlayType.Single,
    windowRef: gameWindow,
    fsb,
    widgetId
  }: {
    table: CasinoTable | CasinoInterruptedGame;
    game?: CasinoGame;
    raceId?: string;
    joinReason?: JoinReason;
    playType?: PlayType;
    windowRef?: Window | null;
    fsb?: PlayerFSB;
    widgetId?: string;
  },
  { rootState, dispatch, request }: EffectParams
) => {
  const tableId = (table as CasinoInterruptedGame).tId || table.id;
  const isMobile = select.device.isMobile(rootState);
  const windowRef = gameWindow || getWindow({ table, playType, rootState });

  dispatch.casino.addLaunch();
  try {
    const { body } = await request({
      service: `casino.games.${joinReason === JoinReason.Resume ? 'resume' : 'launch'}`,
      body: { tableId, raceId, fsbId: table.fsbId, fbCurrency: fsb?.currency }
    });

    dispatch.casino.open({ game, instance: body, windowRef, joinReason, fsb, widgetId });
  } catch (err) {
    const { headers, body } = err as RequestError<{ data: CasinoGameInstance }>;
    const statusCode = headers?.statusCode;
    switch (statusCode) {
      // eslint-disable-next-line no-fallthrough
      case StatusCode.ALREADY_PLAYING_ERROR:
        if (!windowRef || !isRedirected(windowRef) || isMobile) {
          dispatch.casino.open({
            game,
            instance: body.data,
            windowRef,
            joinReason,
            widgetId
          });
        }
        // We've already focused the existing window by calling `getWindow` - do nothing
        return;
      case StatusCode.FSB_ALREADY_IN_PROGRESS:
        dispatch.modal.enqueue({
          path: '/casino/fsb-playing',
          locationState: { ...body, statusCode, table }
        });
        if (!windowRef || isMobile || isRedirected(windowRef)) {
          return;
        }
        break;
      case StatusCode.RACE_PROMO_BAN:
        dispatch.modal.enqueue({
          path: '/casino/race-promo-ban',
          locationState: { ...body, statusCode }
        });
        break;
      default:
        dispatch.modal.enqueue({
          path: '/casino/error',
          locationState: { ...body, statusCode }
        });
    }
    closeWindow(windowRef, isMobile);
  }
};

export default play;
